import { Constants, Types } from '@jumpcloud/ui-components';
import { ItemTypeConsts, StoreModuleMap } from 'client/util/Constants';
import { mapActions, mapGetters } from 'vuex';
import HomeWidgetConsts from 'client/HomePageV2/constants/HomeWidgetConsts';
import Vue from 'vue';
import pluralize from 'pluralize';

const { sortDirections } = Constants;
const { Column } = Types;
const {
  widgetDisplayName,
  widgetNames,
  widgetTableColumns,
} = HomeWidgetConsts;
const {
  deviceInactive,
  reportsReadyForDownload,
  ScheduledUserActivations,
  ScheduledUserSuspensions,
  manualGroupMembership,
  userGroupSuggestions,
} = HomeWidgetConsts.widgetTypes;
const administratorModel = 'AdministratorModel';
const systemUserCollection = StoreModuleMap[ItemTypeConsts.systemUser];

export default {
  data() {
    return {
      sortDirection: sortDirections.ascending,
      currentPageNum: 0,
      data: [],
      deviceInactive,
      filteredData: [],
      filteredByData: [],
      indeterminateSelectAll: false,
      manualGroupMembership,
      selectAll: false,
      isListLoading: false,
      reportsReadyForDownload,
      rowsPerPage: 10,
      tableData: [],
      totalCount: 0,
      ScheduledUserActivations,
      ScheduledUserSuspensions,
      selectedIds: [],
      shouldFocusSearch: false,
      searchTerm: '',
      search: false,
      sortField: 'displayName',
      userGroupSuggestions,
    };
  },

  watch: {
    resetKey: {
      async handler() {
        this.isListLoading = true;
        this.searchTerm = '';
        this.search = false;
        this.filteredData = [];
        this.clearSearchTerm();
        await this.fetchTableData();
      },
    },

    loading(currentValue) {
      this.isListLoading = currentValue;
    },
  },

  computed: {
    ...mapGetters(systemUserCollection, [
      'getDisplayName',
    ]),

    displayName() {
      return widgetNames[this.widgetName];
    },

    toolBarDisplayName() {
      return widgetDisplayName[this.widgetName];
    },

    tableColumns() {
      return widgetTableColumns[this.widgetName].map((column) => new Column(column));
    },

    totalCountDisplay() {
      if (!this.isListLoading) {
        if (this.widgetName !== userGroupSuggestions) {
          return ` - ( ${pluralize('users', this.totalCount, true)} )`;
        }
        return ` - ( ${pluralize('suggestions', this.totalCount, true)} )`;
      }
      return '';
    },
  },

  methods: {

    ...mapActions(administratorModel, ['resetActionKey']),

    handleSelect(selectedItem) {
      const foundDataIndex = this.data.findIndex((item) => item.id === selectedItem.id);
      const selectedItemValue = this.data[foundDataIndex];
      const newValue = {
        ...selectedItemValue,
        selected: !selectedItemValue.selected,
      };
      Vue.set(this.data, foundDataIndex, newValue);

      const foundTableDataIndex = this.tableData.findIndex((item) => item.id === selectedItem.id);
      const selectedTableDataValue = this.tableData[foundTableDataIndex];
      const newTableValue = {
        ...selectedTableDataValue,
        selected: !selectedTableDataValue.selected,
      };
      Vue.set(this.tableData, foundTableDataIndex, newTableValue);

      if (this.selectAll && !this.indeterminateSelectAll) {
        this.selectAll = false;
      }

      const selectedItemIndex = this.data.findIndex((item) => item.selected === true);
      const foundSelectedItem = selectedItemIndex >= 0;
      this.showActions = foundSelectedItem;
      this.indeterminateSelectAll = foundSelectedItem;
    },

    handleSelectAll(value) {
      let selectedItems = [];
      if (this.search) {
        selectedItems = this.filteredData.map((item) => ({
          ...item,
          selected: value,
        }));

        this.filteredData = selectedItems;
        const start = this.currentPageNum * this.rowsPerPage;
        const end = start + this.rowsPerPage;
        this.tableData = this.filteredData.slice(start, end);
        const mergedData = Object.values(this.data.concat(this.filteredData).reduce((r, o) => ({
          ...r,
          [o.id]: o,
        }), {}));
        this.data = mergedData;
      } else {
        selectedItems = this.data.map((item) => ({
          ...item,
          selected: value,
        }));
        this.data = selectedItems;
        const start = this.currentPageNum * this.rowsPerPage;
        const end = start + this.rowsPerPage;
        this.tableData = this.data.slice(start, end);
      }
      this.selectAll = true;
      this.showActions = value && this.data.length;
      this.indeterminateSelectAll = false;
    },

    async clearSearchTerm() {
      await this.handleEnterSearchTerm('');
      this.shouldFocusSearch = true;
    },

    async handleEnterSearchTerm(searchTerm) {
      this.searchTerm = searchTerm;

      if (this.searchTerm !== '') {
        this.search = true;
      } else {
        this.search = false;

        /**
         * This condition is specfic for User Group Suggestions Widget
         * If the filteredData.length check is to ensure overall search clear works.
         *   Checks if the Search without using any filters
         * The second condition checks if any filter value is selected
         *   at the time of clearing the search
         */

        if (this.widgetName === userGroupSuggestions) {
          if (this.filteredByData.length === 0 || this.filterValue === 'all') {
            this.data = this.origData;
          } else {
            this.filteredByData = this.origData.filter(
              (item) => item.groupid === this.filterValue,
            );
            this.data = this.filteredByData;
          }
        }
      }
      if (this.widgetName !== userGroupSuggestions) {
        await this.fetchTableData();
      }

      if (this.filterValue !== 'all' && this.filterValue === !undefined) {
        switch (this.filterValue) {
          case 'passStatus':
            this.filteredByData = this.data.filter((item) => item.passwordStatus === 'Password pending');
            break;
          case 'mfaNotEnrolled':
            this.filteredByData = this.data.filter((item) => item.mfaStatus === 'NOT_ENROLLED');
            break;
          case 'mfaEnrolled':
            this.filteredByData = this.data.filter((item) => item.mfaStatus === 'ENROLLED');
            break;
          case undefined:
            this.filteredByData = [...this.data];
            break;
          default:
            break;
        }
        this.data = this.filteredByData;
      }

      this.shouldFocusSearch = false;
      this.currentPageNum = 0;
      const intermediateData = [...this.data];
      this.filteredData = intermediateData.filter((user) => {
        if (Object.keys(user)
          .some((key) => (`${user[key]}`).toLowerCase().includes(this.searchTerm.toLowerCase()))) {
          return user;
        }
        return null;
      });
      const start = this.currentPageNum * this.rowsPerPage;
      const end = start + this.rowsPerPage;
      this.totalCount = this.filteredData.length;

      /**
       *  If FilterBy option is available for the widget, data will be the searched result.
       *  This is required to ensure that the search is restricted to the filter selected.
       *  */
      /* this.data = Object.values(this.data.concat(this.filteredData).reduce((r, o) => ({
        ...r,
        [o.id]: o,
      }), {})); */
      this.data = this.filteredData;
      this.tableData = this.filteredData.slice(start, end);
    },

    async fetchTableData() {
      try {
        this.isListLoading = true;
        switch (this.widgetName) {
          case 'uptimeMetric':
          case deviceInactive: await this.fetchDevices();
            break;
          case manualGroupMembership:
            await this.handleManualGroupMembership();
            break;
          case ScheduledUserSuspensions:
          case ScheduledUserActivations: await this.fetchUserState();
            break;
          case reportsReadyForDownload: await this.handleReportsForDownload();
            break;
          case userGroupSuggestions: await this.fetchAllGroupsData();
            break;
          default: await this.fetchSystemUsers();
        }
      } catch (exp) {
        this.hasError = true;
        this.isListLoading = false;
      }
    },

    handlePageChange(paginationData) {
      this.isListLoading = true;
      if (paginationData) {
        const { currentPageNum, rowsPerPage } = paginationData;
        this.currentPageNum = currentPageNum;
        this.rowsPerPage = rowsPerPage;
      }
      const start = this.currentPageNum * this.rowsPerPage;
      const end = start + this.rowsPerPage;

      if (this.search && this.filterValue === 'all' && this.filteredData.length > 0) {
        this.tableData = this.filteredData.slice(start, end);
      } else if (!this.search && this.filterValue === undefined) {
        this.tableData = this.data.slice(start, end);
      } else if (this.search && this.filterValue === undefined) {
        this.tableData = this.filteredData.slice(start, end);
      } else if (this.search && this.filterValue !== 'all') {
        this.tableData = this.filteredByData.slice(start, end);
      } else {
        this.tableData = this.data.slice(start, end);
      }
      this.isListLoading = false;
    },

    async handleSortColumn(payload = {}) {
      const {
        dataFieldName = this.sortBy,
        sortDirection = this.sortDirection,
      } = payload;
      this.sortField = `${this.sortDirection === sortDirections.ascending ? '-' : ''}${dataFieldName}`;

      // if uptime column sorting then sort based on timestamp attribute
      if (this.sortField === '-upTime' || this.sortField === 'upTime') {
        this.sortField = this.sortField === '-upTime' ? '-timeStamp' : 'timeStamp';
      }

      this.sortBy = dataFieldName;
      this.sortDirection = sortDirection;
      let sortedData = [];
      if (this.search) {
        sortedData = this.filteredData.sort(this.dynamicSort());
      } else {
        sortedData = this.data.sort(this.dynamicSort());
      }
      const start = this.currentPageNum * this.rowsPerPage;
      const end = start + this.rowsPerPage;
      this.tableData = sortedData.slice(start, end);
    },

    dynamicSort() {
      let sortOrder = 1;
      if (this.sortField[0] === '-') {
        sortOrder = -1;
        this.sortField = this.sortField.substr(1);
      }
      return (a, b) => {
        let result = 0;
        if (typeof (a[this.sortField] + b[this.sortField]) === 'number') {
          if (a[this.sortField] < b[this.sortField]) {
            result = -1;
          } else if (a[this.sortField] > b[this.sortField]) {
            result = 1;
          }
          return result * sortOrder;
        }

        if ((a[this.sortField] < b[this.sortField])) {
          result = -1;
        } else if (a[this.sortField] > b[this.sortField]) {
          result = 1;
        }
        return result * sortOrder;
      };
    },
  },
};
